import { IEssayQuestion, isEssayQuestion, Question, UploadProgressArgs, useSnackbar } from '@eduact/ed-components'
// Vendored: keys options by id instead of text (see Components/VendoredExam/VENDORED.md).
import { TestQuestion } from '@Components/VendoredExam'
import { ANSWER_ATTACHMENT_ACCEPT, parseAttachmentAnswer, serializeAttachmentAnswer } from '@Components/VendoredExam/attachmentAnswer'
import { FormQuestion, useTest } from '@Features/Test/Providers/TestProvider'
import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import useSubmitTest from '@Features/Test/Hooks/useSubmitTest'
import useSaveAnswer from '@Features/Test/Hooks/useSaveAnswer'
import { SnackbarConfig } from '@Utilities/SnackbarConfig'
import { useTranslation } from 'react-i18next'
import NumberFormatter from '@Utilities/NumberFormatter'
import { useAppSelector } from '@Hooks/ReduxHooks'
import styled from 'styled-components'
import { uploadFileWithProgress } from '@Features/Base/BaseApi/BaseApi'
//
const SingleViewMode = () => {
	const { formQuestions, setFormQuestions, test, formQuestionsRef, flushPendingAnswers, registerAnswerControl } = useTest()
	const [currentIndex, setCurrentIndex] = useState(0)
	const [searchParams, setSearchParams] = useSearchParams()
	const { t } = useTranslation(['test'])
	const { isOnline } = useAppSelector(state => state.settings)

	useEffect(() => {
		const firstQuestionIndex = formQuestions.findIndex(_ => _.answer === null)
		if (firstQuestionIndex > -1) {
			setCurrentIndex(firstQuestionIndex)
			searchParams.set('q', formQuestions[firstQuestionIndex].id.toString())
			setSearchParams(searchParams)
		} else {
			setCurrentIndex(formQuestions.length - 1)
		}
	}, [])

	const currentQuestionId = useMemo(() => {
		return Number(searchParams.get('q'))
	}, [searchParams])

	const currentQuestion = useMemo(() => {
		const question = formQuestions.find(_ => _.id === currentQuestionId)
		return question ?? formQuestions[currentIndex]
	}, [currentIndex, currentQuestionId, formQuestions])

	useEffect(() => {
		if (formQuestions.length > 0) {
			const question = formQuestions.findIndex(_ => _.id === currentQuestionId)
			setFormQuestions(draft => {
				if (question > -1) draft[question].touched = true
			})
			if (question > -1) {
				searchParams.set('q', formQuestions[question].id.toString())
				setCurrentIndex(question)
				setSearchParams(searchParams)
			}
		}
	}, [currentQuestion])

	const submitTest = useSubmitTest()
	const saveAnswer = useSaveAnswer()
	const { openSnackbar } = useSnackbar()

	/**
	 * Apply an answer to state and start its save, imperatively.
	 *
	 * By id, never by `currentIndex`: an editor's unmount flush runs in the
	 * passive phase, after the student has already moved on. State is always
	 * kept — offline only defers the POST (the final submit re-sends every
	 * unconfirmed answer). The save is created synchronously here, which is what
	 * lets the submit path flush an editor and then WAIT for the resulting save
	 * instead of racing an effect (the old effect-based autosave and the final
	 * submit could both send the same answer, and the server deletes the row
	 * before re-inserting it).
	 */
	const applyAnswer = (question: Pick<FormQuestion, 'id' | 'type'>, answer: any) => {
		setFormQuestions(draft => {
			const target = draft.find(_ => _.id === question.id)
			if (!target) return
			target.answer = answer
			target.submitted = false
		})
		if (answer !== null && isOnline) saveAnswer(question, answer)
	}

	const handleOnNext = async (order: number, index: number) => {
		// Whatever is still in the essay editor's debounce lands in state first,
		// so the guard and the submit see what the student actually typed.
		flushPendingAnswers()
		const latest = formQuestionsRef.current.find(_ => _.id === currentQuestion.id) ?? currentQuestion
		if ((latest.answer === null || (isEssayQuestion(latest) && latest.answer !== null && latest.answer.answer === null)) && !test.allow_movement) {
			openSnackbar({ ...SnackbarConfig.warning, text: t('must_answer_question') })
			return
		}

		if (index === formQuestions.length - 1) {
			await submitTest()
			return
		}
		if (index < formQuestions.length - 1) {
			setCurrentIndex(index + 1)
			searchParams.set('q', formQuestions[index + 1].id.toString())
			setSearchParams(searchParams)
		}
	}
	const handleOnPrev = async (order: number, index: number) => {
		setCurrentIndex(index - 1)
		searchParams.set('q', formQuestions[index - 1].id.toString())
		setSearchParams(searchParams)
	}

	const [uploadProgress, setUploadProgress] = useState<UploadProgressArgs | undefined>()
	const onAttachmentsChange = async (files: FileList | null, question: Question) => {
		if (!files || files.length === 0) return
		const formData = new FormData()
		for (let index = 0; index < files.length; index++) {
			const file = files.item(index)
			if (!file) return
			if (file.size / 1024 / 1024 > 5) {
				openSnackbar({ ...SnackbarConfig.disclaimer, text: t('file_too_large', { name: file.name }) })
				return
			}
			formData.set('file', file as Blob)
			formData.set('folder', 'answers')
			try {
				const {
					data: {
						data: { uri },
					},
				} = await uploadFileWithProgress({
					formData,
					progressCallback(progress) {
						setUploadProgress({
							file,
							progress,
						})
					},
				})
				setUploadProgress(undefined)
				if (!isEssayQuestion(question)) return
				// Read the CURRENT answer from the ref (a second file in the same
				// selection must see the first one) and merge outside immer: a
				// throw inside a producer surfaces during render, not here.
				const previous = (formQuestionsRef.current.find(_ => _.id === question.id) as IEssayQuestion | undefined)?.answer
				const urls = previous && previous.type === 'attachment' ? parseAttachmentAnswer(previous.answer) : []
				applyAnswer(question, { type: 'attachment', answer: serializeAttachmentAnswer([...urls, uri]) })
			} catch (error) {
				setUploadProgress(undefined)
				openSnackbar({ ...SnackbarConfig.disclaimer, text: t('file_upload_failed') })
			}
		}
	}

	return (
		<TestWrapper online={isOnline}>
			<>
				{currentQuestion && (
					<TestQuestion
						key={`${currentQuestion.id}`}
						question={currentQuestion}
						index={currentIndex}
						onChange={val => applyAnswer(currentQuestion, val)}
						onNext={handleOnNext}
						onPrev={handleOnPrev}
						showPrev={currentIndex > 0}
						nextLabel={currentIndex === formQuestions.length - 1 ? t('submit') : t('next')}
						prevLabel={t('prev')}
						withNavigation
						questionLabel={`${t('q')}. ${NumberFormatter(currentIndex + 1)}`}
						scoreLabel={t('score')}
						essayQuestionProps={{
							onAttachmentsChange(files: FileList | null) {
								onAttachmentsChange(files, currentQuestion)
							},
							onAnswerControl: (control: { flush(): unknown; cancel(): void } | null) => registerAnswerControl(currentQuestion.id, control),
							uploadProgress,
							accept: ANSWER_ATTACHMENT_ACCEPT,
							textChangeDelay: 1000,
						}}
					/>
				)}
			</>
		</TestWrapper>
	)
}

export default SingleViewMode

const TestWrapper = styled.div<{ online: boolean }>`
	pointer-events: ${props => !props.online && 'none'};
	opacity: ${props => !props.online && '0.5'};
`
